import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PhoneInputComponent } from '../../../../shared/components/phone-input/phone-input.component';
import { PasswordChecklist } from '../../../../shared/components/password-checklist/password-checklist';
import { passwordPolicyValidator } from '../../../../core/auth/password-policy';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { AuthService } from '../../../../core/services/auth.service';
import { SettingsService } from '../../../../core/services/settings.service';
import { UploadService } from '../../../../core/services/upload.service';
import { toast } from '../../../../shared/utils/toast';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
    selector: 'app-profile-tab',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, AppButton, PhoneInputComponent, PasswordChecklist],
    templateUrl: './profile-tab.html',
})
export class ProfileTab implements OnInit {
    private authService = inject(AuthService);
    private settingsService = inject(SettingsService);
    private uploadService = inject(UploadService);
    private fb = inject(FormBuilder);
    private cdr = inject(ChangeDetectorRef);
    private translate = inject(TranslateService);
    private sanitizer = inject(DomSanitizer);

    profileForm!: FormGroup;
    passwordForm!: FormGroup;

    currentUserEmail: string = '';
    currentUserId: number | null = null;
    currentUserUuid: string | null = null;
    currentPhoto: string | null = null;
    photoPreview: SafeUrl | null = null;
    newPhotoFile: string | null = null;

    savingProfile = false;
    updatingPassword = false;
    showPassword = false;
    showConfirmPassword = false;
    uploadingPhoto = false;

    ngOnInit() {
        this.initForms();
        this.loadUserProfile();
    }

    initForms() {
        this.profileForm = this.fb.group({
            name: ['', Validators.required],
            phone: [null, Validators.required],
            photo: ['']
        });

        this.passwordForm = this.fb.group({
            password: ['', [Validators.required, passwordPolicyValidator]],
            confirmPassword: ['', Validators.required]
        }, { validators: this.passwordMatchValidator });
    }

    passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
        const password = control.get('password');
        const confirmPassword = control.get('confirmPassword');

        if (password && confirmPassword && password.value !== confirmPassword.value) {
            return { mismatch: true };
        }
        return null;
    }

    async loadUserProfile() {
        try {
            const session = this.authService.session;
            if (!session?.user) return;

            this.currentUserUuid = session.user.id;
            this.currentUserEmail = session.user.email || '';

            const { data } = await this.authService.queryUsersTable(this.currentUserUuid!);

            if (data && data.length > 0) {
                const user = data[0];
                this.currentUserId = user.id;
                this.currentPhoto = user.photo;
                this.profileForm.patchValue({
                    name: user.name,
                    phone: user.cell_phone,
                    photo: user.photo
                });
            }
        } catch (err) {
            console.error('Error loading profile:', err);
            toast.error(this.translate.instant('error_loading_profile'));
        }
    }

    onFileSelected(event: any) {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result as string;
                this.photoPreview = this.sanitizer.bypassSecurityTrustUrl(base64String);
                this.newPhotoFile = base64String;
                this.cdr.detectChanges();
            };
            reader.readAsDataURL(file);
        }
    }

    async updateProfile() {
        if (this.profileForm.invalid || !this.currentUserId) {
            this.profileForm.markAllAsTouched();
            return;
        }

        this.savingProfile = true;
        try {
            const { name, phone } = this.profileForm.value;
            let photoUrl = this.currentPhoto;

            if (this.newPhotoFile) {
                this.uploadingPhoto = true;
                photoUrl = await firstValueFrom(
                    this.uploadService.uploadImage(this.newPhotoFile, 'user', `profile/user_${this.currentUserId}`)
                );
                this.uploadingPhoto = false;
            }

            await firstValueFrom(
                this.settingsService.updateUserProfile(
                    this.currentUserId,
                    name,
                    phone,
                    photoUrl
                )
            );

            this.currentPhoto = photoUrl;
            this.newPhotoFile = null;
            this.photoPreview = null;

            toast.success(this.translate.instant('profile_updated_success'));
        } catch (err) {
            console.error('Error updating profile:', err);
            toast.error(this.translate.instant('error_updating_profile'));
        } finally {
            this.savingProfile = false;
            this.uploadingPhoto = false;
            this.cdr.detectChanges();
        }
    }

    async updatePassword() {
        if (this.passwordForm.invalid) {
            this.passwordForm.markAllAsTouched();
            return;
        }

        this.updatingPassword = true;
        try {
            const { password } = this.passwordForm.value;

            const { error } = await this.authService.updatePassword(password);

            if (error) throw error;

            toast.success(this.translate.instant('password_updated_success'));
            this.passwordForm.reset();
        } catch (err: any) {
            console.error('Error updating password:', err);
            toast.error(err.message || this.translate.instant('error_updating_password'));
        } finally {
            this.updatingPassword = false;
            this.cdr.detectChanges();
        }
    }

    togglePasswordVisibility() {
        this.showPassword = !this.showPassword;
    }

    isFieldInvalid(form: FormGroup, field: string): boolean {
        const control = form.get(field);
        return !!(control && control.invalid && (control.dirty || control.touched));
    }
}
